import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { Alert, Button, Col, Form, Input, Spin, Checkbox, Select, message } from "antd";
import { capitalize } from "lodash";
import isEmpty from "lodash/isEmpty";
import isUndefined from "lodash/isUndefined";
import orderBy from "lodash/orderBy";
import React, { useEffect, useMemo, useRef, useState } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { DEFAULT_COUNTRY } from "../../../config";
import { ADD_USER, UPDATE_USER } from "../../../graphql/mutation";
import { GET_DEPARTMENTS, GET_USER_BY_ID } from "../../../graphql/query";
import useLang from "../../../hooks/useLang";
import { colorConfig } from "../../../library/colorConfig";
import { closeDialog, isEmailValid } from "../../../library/helpers";
import TextInput from "../../../library/textInput";

const validateUserInputs = (user, setError) => {
  let requirmentsSatisfied = false;
  const emailValidated = isEmailValid(user.email);
  if (!emailValidated) {
    setError("You have entered an invalid email address");
  }
  if (
    user.email.trim() !== "" &&
    user.firstName.trim() !== "" &&
    user.lastName.trim() !== "" &&
    !isUndefined(user.roleId)
  ) {
    requirmentsSatisfied = true;
  } else {
    setError("All fields required");
  }
  return emailValidated && requirmentsSatisfied;
};

const parsePermissions = role => {
  const permissions = {
    canCreateUsers: "Users",
    canCreateTeams: "Teams",
    canViewProspects: "Prospects",
    isCompanyAdmin: "Company Admin",
  };
  let permissionString = "";
  Object.entries(role).map(([p, val]) => {
    permissionString += permissions[p] && val === true ? `${permissions[p]}, ` : "";
  });
  return permissionString
    ? permissionString.substring(0, permissionString.length - 2)
    : "No Permissions";
};

const UserForm = ({
  company,
  rolesData,
  locationData,
  companyRoles,
  mode,
  user,
  refetchUsers,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [lang] = useLang();
  const [enableMFA, setEnableMFA] = useState(false);
  const [mfaMethods, setMFAMethods] = useState([]);

  useEffect(() => {
    if (mode === "EDIT") {
      form.setFieldsValue({
        ...user,
        roleId: user?.role?.id || null,
      });
    } else {
      form.resetFields();
    }
  }, [mode]);

  useEffect(() => {
    form.setFieldsValue({
      ...user,
      enableMFA: enableMFA,
    });
  }, [enableMFA]);

  const [updateUser, { loading: updateUserLoader }] = useMutation(UPDATE_USER);
  const [createUser, { loading: createUserLoader }] = useMutation(ADD_USER);
  const close = () => {
    if (onClose) {
      onClose();
    } else {
      closeDialog("userForm");
    }
  };
  const submitUser = async values => {
    if (mode === "EDIT") {
      const res = await updateUser({
        variables: {
          ...values,
          userId: user.id,
          companyId: company.id,
          settings: {
            MFA_ENABLED: enableMFA,
            MFA_METHODS: enableMFA ? mfaMethods : [],
          },
        },
      });
      if (res.data.updateUser.statusCode === 200) {
        message.success(res.data.updateUser.message);
        refetchUsers && refetchUsers();
        close();
      } else {
        message.error(res.data.updateUser.message);
      }
    } else {
      const res = await createUser({
        variables: {
          ...values,
          companyId: company.id,
          settings: JSON.stringify({
            MFA_ENABLED: enableMFA,
            MFA_METHODS: enableMFA ? mfaMethods : [],
          }),
        },
      });
      if (res.data.createUser.statusCode === 200) {
        message.success(res.data.createUser.message);
        refetchUsers && refetchUsers();
        close();
      } else {
        message.error(res.data.createUser.message);
      }
    }
  };

  const { Option } = Select;

  return (
    <Spin spinning={updateUserLoader || createUserLoader}>
      <Form form={form} onFinish={submitUser} layout="vertical" className="w-full">
        <Form.Item
          name="email"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Please enter a valid email" },
          ]}
          label="Email address"
        >
          <Input placeholder={lang.emailLabel} name="email" />
        </Form.Item>
        <Form.Item
          name="firstName"
          rules={[{ required: true, message: "Please enter first name" }]}
          label="First name"
        >
          <Input placeholder={lang.firstName} name="firstName" />
        </Form.Item>
        <Form.Item
          name="lastName"
          rules={[{ required: true, message: "Please enter last name" }]}
          label="Last name"
        >
          <Input placeholder={lang.lastName} name="lastName" />
        </Form.Item>
        <Form.Item name="phone" label="Phone number">
          <PhoneInput
            name="phone"
            international
            withCountryCallingCode
            countryCallingCodeEditable={false}
            defaultCountry={DEFAULT_COUNTRY}
            placeholder={lang.phoneNumber}
            label={lang.phoneNumber}
            className="phoneInputCss py-1 w-full"
          />
        </Form.Item>
        <Form.Item name="roleId" label={lang.selectRole}>
          <Select className="w-full" name="roleId" placeholder={lang.selectRole} showArrow>
            {rolesData &&
              rolesData.roles.map(role => (
                <Option key={role.id}>
                  {role.name} -{" "}
                  <span className={`text-[12px] text-[${colorConfig.medDarkgray}]`}>
                    {parsePermissions(role)}
                  </span>
                </Option>
              ))}
          </Select>
        </Form.Item>
        <Form.Item name="companyRoleId" label={lang.selectCompanyRole}>
          <Select className="w-full" name="companyRole" placeholder={lang.selectCompanyRole}>
            {(companyRoles || []).map(item => (
              <Option key={item.id} value={+item.id}>
                {item.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label={lang.selectLocation}
          name="locationId"
          rules={[{ required: true, message: "Please select a location" }]}
        >
          <Select
            className="w-full"
            placeholder={lang.selectLocation}
            showArrow
            name="locationId"
            onChange={value => {
              const selectedLocation = locationData?.find(el => +el.id === +value);
              if (selectedLocation?.settings) {
                const settings = JSON.parse(selectedLocation.settings);
                setEnableMFA(settings?.MFA_ENABLED || false);
                setMFAMethods(settings?.MFA_METHODS || []);
              }
            }}
          >
            {(locationData || []).map(item => (
              <Option key={item.id} value={+item.id}>
                {item.title}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Enable MFA for Sign-In" name="enableMFA" valuePropName="checked">
          <Checkbox checked={enableMFA} onChange={() => setEnableMFA(!enableMFA)}>
            Enable MFA
          </Checkbox>
        </Form.Item>
        {enableMFA && (
          <Form.Item
            label="MFA Methods"
            name="mfaMethod"
            rules={[{ required: enableMFA, message: "Please select an MFA method" }]}
          >
            <div style={{ marginTop: "10px" }}>
              <Checkbox
                checked={mfaMethods?.includes("SOFTWARE_TOKEN_MFA")}
                onChange={e => {
                  const updatedMethods = e.target.checked
                    ? [...(mfaMethods || []), "SOFTWARE_TOKEN_MFA"]
                    : mfaMethods?.filter(method => method !== "SOFTWARE_TOKEN_MFA");
                  setMFAMethods(updatedMethods);
                }}
              >
                Authenticator App
              </Checkbox>
              {/* <Checkbox
              checked={mfaMethods?.includes("EMAIL_MFA")}
              onChange={e => {
                const updatedMethods = e.target.checked
                  ? [...(mfaMethods || []), "EMAIL_MFA"]
                  : mfaMethods?.filter(method => method !== "EMAIL_MFA");
                setMFAMethods(updatedMethods);
              }}
            >
              Email
            </Checkbox>
            <Checkbox
              checked={mfaMethods?.includes("SMS_MFA")}
              onChange={e => {
                const updatedMethods = e.target.checked
                  ? [...(mfaMethods || []), "SMS_MFA"]
                  : mfaMethods?.filter(method => method !== "SMS_MFA");
                setMFAMethods(updatedMethods);
              }}
            >
              SMS
            </Checkbox> */}
            </div>
          </Form.Item>
        )}

        <div className="flex-row-centered justify-end gap-2">
          <Button onClick={close}>Cancel</Button>
          <Button
            type="primary"
            loading={updateUserLoader || createUserLoader}
            onClick={() => form.submit()}
          >
            {capitalize(mode === "EDIT" ? lang.save : lang.add)}
          </Button>
        </div>
      </Form>
    </Spin>
  );
};

export default UserForm;
